import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import AppContainer from "../../common/app_container";
import Alert, {AlertProps} from "../../common/alert";
import {SubmitHandler, useForm} from "react-hook-form";
import { PromptDTO } from "@src/model/prompt";
import { usePromptAPI } from "@src/api/prompt_api";

const PromptEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getPromptById, createPrompt, updatePrompt } = usePromptAPI();
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(!!id);
    const isEditMode = !!id;

    const { register, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm<PromptDTO>();

    useEffect(() => {
        if (isEditMode) {
            setIsFetching(true);
            setStatus(null);
            getPromptById(id!)
                .then(codeData => {
                    reset(codeData);
                })
                .catch((err: any) => setStatus({ type: "error", message: `Failed to load code: ${err.message}` }))
                .finally(() => setIsFetching(false));
        }
    }, [id]);

    const onSubmit: SubmitHandler<PromptDTO> = async (formData) => {
        setIsLoading(true);
        setStatus(null);

        try {
            let result: PromptDTO;
            if (isEditMode) {
                result = await updatePrompt(id!, formData);
                setStatus({ type: "success", message: "Prompt updated successfully." });
                reset(formData);
            } else {
                result = await createPrompt(formData);
                setStatus({ type: "success", message: "Code created successfully." });
                navigate(`/prompts/edit/${result.id}`, { replace: true });
            }
        } catch (error: any) {
            setStatus({ type: "error", message: `Failed to save prompt: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };


    if (isFetching) {
        return <AppContainer><div className="p-6">Loading prompt details...</div></AppContainer>;
    }

    return (
        <AppContainer>
            <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">{isEditMode ? "Edit Prompt" : "Create Prompt"}</h1>
                    <button
                        onClick={() => navigate("/prompts")}
                        className="px-4 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-700"
                    >
                        Back to List
                    </button>
                </div>

                {status && <Alert {...status} onClose={() => setStatus(null)} className="mb-4" />}

                <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-6 rounded shadow border space-y-4">
                    <div>
                        <label htmlFor="entity_type" className="block text-sm font-medium text-gray-700 mb-1">Entity Type (Optional)</label>
                        <select
                            id="entity_type"
                            {...register("entity_type", {
                                onChange:(e) => {
                                  e.target.value = e.target.value.toUpperCase();
                              }
                            })}
                            className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.entity_type ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                        >
                            <option value=''>-- Select Entity Type --</option>
                            <option value="LICENSABLE_PROPERTY">LICENSABLE_PROPERTY</option>
                            <option value="USER_PRODUCT">USER_PRODUCT</option>
                            <option value="BRAND">BRAND</option>
                        </select>
                        {errors.entity_type?.message && <p className="text-red-500 text-xs mt-1">{String(errors.entity_type.message)}</p>}
                    </div>

                    <div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                {...register("active")}
                                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            />
                            <span className="text-sm text-gray-700">Active</span>
                        </label>
                    </div>

                    <div>
                        <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
                        <textarea
                            id="prompt"
                            {...register("prompt", { required: true  })}
                            className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.prompt ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                            rows={3}
                        />
                        {errors.prompt?.message && <p className="text-red-500 text-xs mt-1">{String(errors.prompt.message)}</p>}
                    </div>


                    <div className="flex justify-end pt-4 border-t">
                        <button
                            type="submit"
                            disabled={isLoading || !isDirty}
                            className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 text-sm font-medium"
                        >
                            {isLoading ? "Saving..." : isEditMode ? "Save Changes" : "Create Prompt"}
                        </button>
                    </div>
                </form>
            </div>
        </AppContainer>
    );
};

export default PromptEdit;
